import { useId } from 'react';

/**
 * A real on/off switch (WAI-ARIA switch pattern): a native button, so Space and
 * Enter work and focus is visible, with role="switch" and aria-checked carrying
 * the state to assistive technology. The visible label names it; the On/Off
 * text beside it says the state in words, so color is never the only signal.
 *
 * Size (design system, Appendix G.1): a 34 x 20 px track with a 14 px thumb and 2 px radius on both (the square
 * language). The visible track stays small; `tap-area` keeps the hit area at least 38 x 40 px (never below 24 x 24).
 *
 * Site tokens: on = #0066B3, off = neutral #E5E7EB with a #364153 knob border.
 * `onChange` receives the next value. A disabled switch keeps its state, dims
 * and takes no pointer or keyboard input.
 */
export default function Toggle({ checked, onChange, label, disabled = false, showState = true, describedBy, ariaLabel, className = '' }) {
  const labelId = useId();
  const on = checked === true;
  return (
    <span className={`inline-flex items-center gap-2 text-sm font-medium text-slate-800 ${className}`}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-labelledby={ariaLabel ? undefined : labelId}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => { if (!disabled) onChange(!on); }}
        className={`tap-area relative inline-flex h-5 w-[34px] shrink-0 items-center rounded-sm border border-[#364153] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0066B3] focus-visible:ring-offset-2 ${on ? 'bg-[#0066B3]' : 'bg-[#E5E7EB]'} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        <span
          aria-hidden="true"
          className={`inline-block h-3.5 w-3.5 rounded-sm border bg-white transition-transform ${on ? 'translate-x-4 border-white' : 'translate-x-0.5 border-[#364153]'}`}
        />
      </button>
      <span id={labelId}>{label}</span>
      {showState ? <span className="text-slate-700" data-testid="toggle-state">{on ? 'On' : 'Off'}</span> : null}
    </span>
  );
}
